import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { Settings, shouldUpdate } from '@parkingless/interface';
import { DataService } from './data.service';
import { SessionService } from './session.service';
import { consoleVersion } from '../config/version';
import { message } from '../config/messages';

/** What the running console has to do about the version the settings document names. */
export type ConsoleUpdate = 'none' | 'soft' | 'force';

/**
 * Whether this build is behind `settings/v1.garageApp.web.currentVersion`, and whether
 * `forceUpdate` beside it withdraws it.
 *
 * The same question the three apps ask of their own entry in the document, through the
 * same `shouldUpdate` — so a version nobody can parse is never an update, and a typo in
 * the document cannot lock the whole console out.
 */
export function consoleUpdate(settings: Settings | null, localVersion: string): ConsoleUpdate {
  const web = settings?.garageApp?.web;
  if (!web?.currentVersion || !shouldUpdate({ currentVersion: web.currentVersion, localVersion })) {
    return 'none';
  }

  return web.forceUpdate === true ? 'force' : 'soft';
}

/**
 * Watches the version in the settings document for the life of the application.
 *
 * A browser tab has no store to send anyone to: the new build is already on hosting, and
 * updating is reloading. So behind the named version, the console offers a reload in a
 * snackbar that stays until it is taken; with `forceUpdate` it is replaced outright by
 * the blocking page `AppComponent` renders from `state`. Neither reloads on its own — a
 * half-filled form is the user's to lose.
 *
 * Watched live rather than checked at boot, because the tab that needs it is the one left
 * open since yesterday. Raise the version only once the deploy has finished: a reload
 * before then fetches the old build, which is still behind.
 */
@Injectable({ providedIn: 'root' })
export class VersionWatcher {
  private readonly data = inject(DataService);
  private readonly session = inject(SessionService);
  private readonly snackBar = inject(MatSnackBar);
  // The service's own ref, as in MaintenanceWatcher: `start()` may be called from
  // anywhere, and the subscription belongs to the application.
  private readonly destroyRef = inject(DestroyRef);

  readonly localVersion = consoleVersion;
  readonly state = signal<ConsoleUpdate>('none');
  readonly remoteVersion = signal<string | null>(null);

  private offer: MatSnackBarRef<TextOnlySnackBar> | null = null;
  private started = false;

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    this.session.initialize();

    this.data.settings$
      .pipe(
        map((settings) => ({
          state: consoleUpdate(settings, this.localVersion),
          remote: settings?.garageApp?.web?.currentVersion ?? null,
        })),
        distinctUntilChanged((a, b) => a.state === b.state && a.remote === b.remote),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ state, remote }) => this.apply(state, remote));
  }

  /** Reloads the page, which fetches whatever build hosting now serves. */
  reload(): void {
    window.location.reload();
  }

  private apply(state: ConsoleUpdate, remote: string | null): void {
    this.state.set(state);
    this.remoteVersion.set(remote);

    if (state === 'soft') {
      this.offer ??= this.openOffer();
      return;
    }

    // Gone back to none, or escalated to the blocking page, which says it instead.
    this.offer?.dismiss();
    this.offer = null;
  }

  private openOffer(): MatSnackBarRef<TextOnlySnackBar> {
    const ref = this.snackBar.open(message.versionUpdate.softMessage, message.versionUpdate.reload);
    ref.onAction().subscribe(() => this.reload());
    ref.afterDismissed().subscribe(() => {
      if (this.offer === ref) {
        this.offer = null;
      }
    });

    return ref;
  }
}
